import React, { useState, useEffect } from 'react';
import { Save, X, Brain, AlertCircle, Info, History, CheckCircle2, FileText, Database, Eye, ChevronDown } from 'lucide-react';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { SchemaBuilder } from '../../components/schema/SchemaBuilder';
import { EnumDefinitionsManager } from '../../components/admin/EnumDefinitionsManager';
import { organizationApi, aiModelsApi } from '../../services/adminApi';

interface AIConfigForm {
  ai_api_token?: string;
  ai_provider: string;
  ai_model: string;
  ai_prompt: string;
  min_required_images: number;
  ai_analysis_schema?: any;
}

type TabType = 'prompt' | 'schema' | 'definitions';

interface OrganizationAIConfigProps {
  organizationId: string;
  onNavigate: (page: string) => void;
}

export const OrganizationAIConfig: React.FC<OrganizationAIConfigProps> = ({
  organizationId,
  onNavigate,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('prompt');
  const [form, setForm] = useState<AIConfigForm>({
    ai_api_token: '',
    ai_provider: 'anthropic',
    ai_model: 'claude-3-5-sonnet-20241022',
    ai_prompt: '',
    min_required_images: 3,
    ai_analysis_schema: null,
  });

  const [orgName, setOrgName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [originalAIToken, setOriginalAIToken] = useState<string | undefined>(undefined);
  const [testingToken, setTestingToken] = useState(false);
  const [tokenTestResult, setTokenTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);
  const [availableProviders, setAvailableProviders] = useState<any[]>([]);
  const [availableModels, setAvailableModels] = useState<any[]>([]);
  const [previewingPrompt, setPreviewingPrompt] = useState(false);
  const [promptPreview, setPromptPreview] = useState<string | null>(null);
  const [previewingApiRequest, setPreviewingApiRequest] = useState(false);
  const [apiRequestPreview, setApiRequestPreview] = useState<any | null>(null);
  const [templateVarsExpanded, setTemplateVarsExpanded] = useState(true);

  useEffect(() => {
    fetchOrganization();
    fetchAvailableModels();
  }, [organizationId]);

  const fetchOrganization = async () => {
    try {
      setLoading(true);
      const response = await organizationApi.get(organizationId);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch organization');
      }

      const data = response.data;
      setOrgName(data.display_name || data.name);

      // Store whether token exists based on backend flag
      setOriginalAIToken(data.has_ai_token ? '••••••••••••••••' : undefined);

      // Debug logging
      console.log('🔧 Loaded organization data:', {
        ai_provider: data.ai_provider,
        ai_model: data.ai_model,
        ai_prompt: data.ai_prompt,
        min_required_images: data.min_required_images,
      });

      setForm({
        ai_api_token: data.has_ai_token ? '••••••••••••••••' : '',
        ai_provider: data.ai_provider || 'anthropic',
        ai_model: data.ai_model || 'claude-3-5-sonnet-20241022',
        ai_prompt: data.ai_prompt || '',
        min_required_images: data.min_required_images ?? 3,
        ai_analysis_schema: data.ai_analysis_schema || null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load organization');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableModels = async () => {
    try {
      const [providersRes, modelsRes] = await Promise.all([
        aiModelsApi.listProviders(),
        aiModelsApi.listModels(),
      ]);

      if (providersRes.success && providersRes.data) {
        setAvailableProviders(providersRes.data.filter((p: any) => p.is_active));
      }

      if (modelsRes.success && modelsRes.data) {
        setAvailableModels(modelsRes.data.filter((m: any) => m.is_active));
      }
    } catch (err) {
      console.error('Failed to load available models:', err);
      // Don't show error to user - fall back to hardcoded options
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Prepare data for submission
      const submitData = { ...form };

      // Only send token if it was changed from the masked value
      if (submitData.ai_api_token === originalAIToken) {
        // Don't send the masked token - backend will keep existing token
        delete submitData.ai_api_token;
      }

      // Debug logging
      console.log('🔧 Submitting AI config update:', submitData);

      const response = await organizationApi.update(organizationId, submitData);

      if (!response.success) {
        throw new Error(response.message || 'Failed to save AI configuration');
      }

      setSuccessMessage('AI configuration saved successfully! Redirecting...');

      // Navigate back to admin dashboard after short delay
      setTimeout(() => {
        onNavigate('admin-organizations');
      }, 1000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save AI configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof AIConfigForm, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear test result when token or model changes
    if (field === 'ai_api_token' || field === 'ai_model') {
      setTokenTestResult(null);
    }
  };

  const handleTestToken = async () => {
    try {
      setTestingToken(true);
      setTokenTestResult(null);

      // Get the token to test - use form value if it's been changed, otherwise use saved token
      const tokenToTest = form.ai_api_token && form.ai_api_token !== originalAIToken
        ? form.ai_api_token
        : undefined;

      // Pass the currently selected model from the form
      const response = await organizationApi.testAIToken(
        organizationId,
        tokenToTest,
        form.ai_model  // Pass the selected model
      );

      if (response.success && response.data) {
        setTokenTestResult({
          success: response.data.success,
          message: response.data.message,
        });
      } else {
        setTokenTestResult({
          success: false,
          message: response.message || 'Failed to test token',
        });
      }
    } catch (err) {
      setTokenTestResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to test token',
      });
    } finally {
      setTestingToken(false);
    }
  };

  const handleLoadExampleSchema = () => {
    const exampleSchema = {
      version: '1.0.0',
      sections: {
        square_projection: {
          description: 'Customer-facing data synced to Square POS',
          attributes: {
            category: {
              type: 'enum',
              values: ['Kimono', 'Haori', 'Yukata', 'Obi', 'Hakama', 'Other'],
              required: true,
              prompt_hint: 'Primary product category for Square catalog organization',
            },
            audience: {
              type: 'enum',
              values: ['Women', 'Men', 'Unisex', 'Boys', 'Girls', 'Children'],
              required: true,
              prompt_hint: 'Target audience based on sizing and style',
            },
            era: {
              type: 'enum',
              values: ['Meiji', 'Taisho', 'Early Showa', 'Mid Showa', 'Late Showa', 'Heisei', 'Contemporary', 'Unknown'],
              required: true,
              prompt_hint: 'Historical period based on construction and style',
            },
            material: {
              type: 'enum',
              values: ['Silk', 'Rinzu Silk', 'Chirimen', 'Tsumugi', 'Meisen', 'Wool', 'Cotton', 'Synthetic', 'Blend'],
              required: true,
              prompt_hint: 'Primary fabric material',
            },
            primary_color: {
              type: 'color',
              required: true,
              prompt_hint: 'Dominant color',
            },
            secondary_color: {
              type: 'color_or_null',
              required: false,
              prompt_hint: 'Secondary accent color if present',
            },
          },
        },
        internal_record: {
          description: 'Inventory-specific data',
          attributes: {
            condition: {
              type: 'enum',
              values: ['Museum_Quality', 'Excellent', 'Very_Good', 'Good', 'Fair', 'Poor'],
              required: true,
              prompt_hint: 'Overall condition assessment',
            },
            confidence_level: {
              type: 'enum',
              values: ['High', 'Medium', 'Low'],
              required: true,
              prompt_hint: 'Confidence in the analysis accuracy',
            },
            detailed_inventory_description: {
              type: 'string',
              required: true,
              max_length: 5000,
              prompt_hint: 'Comprehensive technical description',
            },
          },
          attributes_rich: [
            { key: 'motifs', type: 'comma_separated', prompt_hint: 'Visual motifs and patterns' },
            { key: 'mon_present', type: 'string', prompt_hint: "'Yes - describe' or 'No'" },
            { key: 'lining', type: 'string', prompt_hint: 'Lining material and condition' },
            { key: 'construction', type: 'enum', values: ['Lined', 'Unlined', 'Awase', 'Hitoe'], prompt_hint: 'Construction type' },
          ],
        },
      },
    };

    handleChange('ai_analysis_schema', exampleSchema);
  };

  // Generate markdown preview for a specific section
  const generateSectionPreview = (sectionName: string): string => {
    if (!form.ai_analysis_schema || !form.ai_analysis_schema.sections) {
      return 'No schema defined yet. Define your schema in the "Attribute Schema" tab.';
    }

    const sections = form.ai_analysis_schema.sections;

    if (sectionName === 'all') {
      // Generate preview for all sections
      const allPreviews = Object.keys(sections).map(key => {
        return generateSingleSectionMarkdown(key, sections[key]);
      }).join('\n\n');
      return allPreviews || 'No sections defined';
    } else {
      // Generate preview for specific section
      const section = sections[sectionName];
      if (!section) {
        return `Section "${sectionName}" not found in schema.`;
      }
      return generateSingleSectionMarkdown(sectionName, section);
    }
  };

  // Generate JSON response schema template
  const generateJSONResponseSchema = (sectionName: string): string => {
    if (!form.ai_analysis_schema || !form.ai_analysis_schema.sections) {
      return 'No schema defined yet. Define your schema in the "Attribute Schema" tab.';
    }

    const sections = form.ai_analysis_schema.sections;

    if (sectionName === 'all') {
      // Generate full response schema with all sections
      const schema: any = {};
      Object.keys(sections).forEach(key => {
        schema[key] = generateSectionJSONSchema(sections[key]);
      });
      return JSON.stringify(schema, null, 2);
    } else {
      // Generate schema for specific section
      const section = sections[sectionName];
      if (!section) {
        return `Section "${sectionName}" not found in schema.`;
      }
      const schema = { [sectionName]: generateSectionJSONSchema(section) };
      return JSON.stringify(schema, null, 2);
    }
  };

  // Helper to generate JSON schema object for a section
  const generateSectionJSONSchema = (section: any): any => {
    const schemaObj: any = {};

    // Add standard attributes
    if (section.attributes) {
      Object.entries(section.attributes).forEach(([key, attr]: [string, any]) => {
        if (attr.type === 'enum' && attr.values) {
          schemaObj[key] = `<${attr.values.join('|')}>`;
        } else if (attr.type === 'string') {
          const hint = attr.prompt_hint || key.replace(/_/g, ' ');
          if (attr.max_length) {
            schemaObj[key] = `<${hint}, max ${attr.max_length} chars>`;
          } else {
            schemaObj[key] = `<${hint}>`;
          }
        } else if (attr.type === 'color') {
          schemaObj[key] = '<color>';
        } else if (attr.type === 'color_or_null') {
          schemaObj[key] = '<color or null>';
        } else if (attr.type === 'number') {
          schemaObj[key] = '<number>';
        } else {
          schemaObj[key] = `<${attr.type}>`;
        }
      });
    }

    // Add attributes_rich if present
    if (section.attributes_rich && section.attributes_rich.length > 0) {
      schemaObj.attributes_rich = section.attributes_rich.map((attr: any) => {
        const hint = attr.prompt_hint || attr.key.replace(/_/g, ' ');
        if (attr.type === 'enum' && attr.values) {
          return { key: attr.key, value: `<${attr.values.join('|')}>` };
        } else {
          return { key: attr.key, value: `<${hint}>` };
        }
      });
    }

    return schemaObj;
  };

  // Helper to generate markdown for a single section
  const generateSingleSectionMarkdown = (name: string, section: any): string => {
    let markdown = `### ${name}\n`;

    if (section.description) {
      markdown += `${section.description}\n\n`;
    }

    markdown += 'Return these attributes:\n';

    if (section.attributes) {
      Object.entries(section.attributes).forEach(([key, attr]: [string, any]) => {
        const required = attr.required ? 'required' : 'optional';
        markdown += `- ${key} (${required})`;

        if (attr.prompt_hint) {
          markdown += `: ${attr.prompt_hint}`;
        }
        markdown += '\n';

        if (attr.type === 'enum' && attr.values) {
          markdown += `  Values: ${attr.values.join(', ')}\n`;
        } else if (attr.max_length) {
          markdown += `  Max length: ${attr.max_length} characters\n`;
        }
      });
    }

    if (section.attributes_rich && section.attributes_rich.length > 0) {
      markdown += '\nAdditionally, include attributes_rich array:\n';
      section.attributes_rich.forEach((attr: any) => {
        markdown += `- ${attr.key}`;
        if (attr.prompt_hint) {
          markdown += `: ${attr.prompt_hint}`;
        }
        markdown += '\n';
      });
    }

    return markdown;
  };

  const handlePreviewPrompt = async () => {
    try {
      setPreviewingPrompt(true);
      setPromptPreview(null);

      // Pass current form values (unsaved changes)
      const response = await organizationApi.previewSchema(
        organizationId,
        form.ai_prompt,
        form.ai_analysis_schema
      );

      if (response.success && response.data) {
        setPromptPreview(response.data.expanded_prompt);
      } else {
        throw new Error(response.message || 'Failed to preview prompt');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview prompt');
    } finally {
      setPreviewingPrompt(false);
    }
  };

  const handlePreviewApiRequest = async () => {
    try {
      setPreviewingApiRequest(true);
      setApiRequestPreview(null);

      // Pass current form values (unsaved changes)
      const response = await organizationApi.previewApiRequest(
        organizationId,
        form.ai_prompt,
        form.ai_analysis_schema
      );

      if (response.success && response.data) {
        setApiRequestPreview(response.data);
      } else {
        throw new Error(response.message || 'Failed to preview API request');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview API request');
    } finally {
      setPreviewingApiRequest(false);
    }
  };

  // Template Variable Box Component with Click-to-Toggle Preview
  const TemplateVariableBox: React.FC<{
    variable: string;
    description: string;
    sectionName: string;
  }> = ({ variable, description, sectionName }) => {
    const [showPreview, setShowPreview] = useState(false);
    const [viewMode, setViewMode] = useState<'markdown' | 'json'>('markdown');
    const markdownPreview = generateSectionPreview(sectionName);
    const jsonPreview = generateJSONResponseSchema(sectionName);

    const handleCopyMarkdown = () => {
      navigator.clipboard.writeText(markdownPreview);
    };

    const handleCopyJSON = () => {
      navigator.clipboard.writeText(jsonPreview);
    };

    const togglePreview = () => {
      setShowPreview(!showPreview);
    };

    return (
      <div className="relative bg-white p-3 rounded border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <code className="text-sm font-mono text-purple-700 font-semibold">{variable}</code>
            <p className="text-xs text-gray-700 mt-1">{description}</p>
          </div>
          <button
            type="button"
            onClick={togglePreview}
            className={`ml-2 p-1.5 rounded-lg transition-colors ${
              showPreview ? 'bg-purple-200' : 'hover:bg-purple-100'
            }`}
            aria-label="Toggle preview of template variable expansion"
          >
            <Eye className={`h-4 w-4 ${showPreview ? 'text-purple-700' : 'text-purple-600'}`} />
          </button>
        </div>

        {/* Click-to-Toggle Preview */}
        {showPreview && (
          <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-gray-900 text-gray-100 p-4 rounded-lg shadow-xl border border-gray-700 max-h-96 overflow-y-auto">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Eye className="h-4 w-4 text-purple-400" />
                <span className="text-xs font-semibold text-purple-400">Live Preview</span>
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex bg-gray-800 rounded overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setViewMode('markdown')}
                    className={`px-2 py-0.5 text-xs transition-colors ${
                      viewMode === 'markdown'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    Markdown
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode('json')}
                    className={`px-2 py-0.5 text-xs transition-colors ${
                      viewMode === 'json'
                        ? 'bg-purple-600 text-white'
                        : 'text-gray-400 hover:text-gray-200'
                    }`}
                  >
                    JSON
                  </button>
                </div>
                {/* Copy Button */}
                <button
                  type="button"
                  onClick={viewMode === 'markdown' ? handleCopyMarkdown : handleCopyJSON}
                  className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                  title={`Copy ${viewMode === 'markdown' ? 'markdown' : 'JSON schema'} to clipboard`}
                >
                  Copy
                </button>
                {/* Close Button */}
                <button
                  type="button"
                  onClick={togglePreview}
                  className="p-1 text-gray-400 hover:text-white transition-colors"
                  title="Close preview"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            <pre className="text-xs font-mono whitespace-pre-wrap break-words">
              {viewMode === 'markdown' ? markdownPreview : jsonPreview}
            </pre>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="h-8 w-8 text-purple-600" />
          <h1 className="text-3xl font-bold text-gray-900">AI Configuration</h1>
        </div>
        <p className="text-gray-600 text-lg">
          Configure AI analysis settings for <strong>{orgName}</strong>
        </p>
      </div>

      {error && (
        <div className="mb-6">
          <ErrorMessage message={error} />
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-green-800">{successMessage}</span>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Tab Navigation */}
          <Card>
            <div className="flex border-b border-gray-200">
              <button
                type="button"
                onClick={() => setActiveTab('prompt')}
                className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'prompt'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <FileText className="h-5 w-5" />
                Prompt Template
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('schema')}
                className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'schema'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Database className="h-5 w-5" />
                Attribute Schema
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('definitions')}
                className={`flex items-center gap-2 px-6 py-3 font-semibold transition-colors ${
                  activeTab === 'definitions'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Info className="h-5 w-5" />
                Definitions
              </button>
            </div>
          </Card>

          {/* Prompt Template Tab */}
          {activeTab === 'prompt' && (
            <>
          {/* Model Selection */}
          <Card>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI Model Selection
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="ai_provider" className="block text-sm font-bold text-gray-900 mb-2">
                  AI Provider
                </label>
                <select
                  id="ai_provider"
                  value={form.ai_provider}
                  onChange={(e) => handleChange('ai_provider', e.target.value)}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  aria-label="AI Provider"
                >
                  {availableProviders.length > 0 ? (
                    availableProviders.map((provider) => (
                      <option key={provider.id} value={provider.name}>
                        {provider.display_name}
                      </option>
                    ))
                  ) : (
                    <>
                      <option value="anthropic">Anthropic (Claude)</option>
                      <option value="openai">OpenAI (GPT)</option>
                    </>
                  )}
                </select>
              </div>

              <div>
                <label htmlFor="ai_model" className="block text-sm font-bold text-gray-900 mb-2">
                  AI Model Version
                </label>
                <select
                  id="ai_model"
                  value={form.ai_model}
                  onChange={(e) => handleChange('ai_model', e.target.value)}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  aria-label="AI Model"
                >
                  {availableModels.length > 0 ? (
                    availableProviders.map((provider) => {
                      const providerModels = availableModels.filter((m) => m.provider_id === provider.id);
                      if (providerModels.length === 0) return null;
                      return (
                        <optgroup key={provider.id} label={provider.display_name}>
                          {providerModels.map((model) => (
                            <option key={model.id} value={model.model_id}>
                              {model.display_name}
                              {model.is_recommended && ' ⭐ (Recommended)'}
                            </option>
                          ))}
                        </optgroup>
                      );
                    })
                  ) : (
                    <optgroup label="Claude Models">
                      <option value="claude-3-5-sonnet-20241022">Claude 3.5 Sonnet (Latest, Balanced)</option>
                      <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku (Fast, Cost-Effective)</option>
                      <option value="claude-opus-4-20250514">Claude Opus 4 (Most Capable, Premium)</option>
                    </optgroup>
                  )}
                </select>
                <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-blue-800">
                      <strong>Model changes are tracked in the audit log</strong> for performance comparison.
                      You can analyze which model works best for your use case.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* AI API Token */}
          <Card>
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Brain className="h-5 w-5 text-purple-600" />
              AI API Token
            </h2>

            <div>
              <label htmlFor="ai_api_token" className="block text-sm font-bold text-gray-900 mb-2">
                API Token
              </label>
              <div className="flex gap-3">
                <input
                  type="password"
                  id="ai_api_token"
                  value={form.ai_api_token || ''}
                  onChange={(e) => handleChange('ai_api_token', e.target.value)}
                  className="flex-1 px-4 py-3 text-lg border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter AI API Token (e.g., sk-ant-...)"
                />
                <Button
                  type="button"
                  onClick={handleTestToken}
                  disabled={testingToken || !form.ai_api_token}
                  variant="outline"
                  size="lg"
                  icon={CheckCircle2}
                  className="border-purple-300 text-purple-600 hover:bg-purple-50"
                >
                  {testingToken ? 'Testing...' : 'Test Token'}
                </Button>
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {originalAIToken
                  ? 'Token is securely stored. Leave as is to keep current token, or enter a new one to update.'
                  : 'Enter your AI provider API token. This will be encrypted and stored securely.'}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                The test will use the model selected above: <strong>{form.ai_model}</strong>
              </p>

              {/* Token test result */}
              {tokenTestResult && (
                <div className={`mt-3 p-3 rounded-lg border ${
                  tokenTestResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start gap-2">
                    {tokenTestResult.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    )}
                    <div>
                      <p className={`text-sm font-semibold ${
                        tokenTestResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {tokenTestResult.success ? 'Token Valid' : 'Token Invalid'}
                      </p>
                      <p className={`text-sm ${
                        tokenTestResult.success ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {tokenTestResult.message}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Image Requirements */}
          <Card>
            <h2 className="text-xl font-semibold mb-4">Image Upload Requirements</h2>

            <div className="space-y-4">
              <div>
                <label htmlFor="min_required_images" className="block text-sm font-bold text-gray-900 mb-2">
                  Minimum Required Images
                </label>
                <input
                  id="min_required_images"
                  type="number"
                  min={1}
                  max={10}
                  value={form.min_required_images}
                  onChange={(e) => handleChange('min_required_images', Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                  className="w-full md:w-64 px-4 py-3 text-lg border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Minimum number of image slots shown when adding products (1–10). Users can add more up to 10 total.
                </p>
              </div>

              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900">Important</h3>
                    <p className="text-sm text-amber-800 mt-1">
                      Make sure your AI prompt reflects the number of images you expect. For example, if you require
                      3 images, your prompt should specify what each image should contain (e.g., "front view, back view, detail closeup").
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Card>

          {/* AI Prompt - Big and Bold */}
          <Card>
            <div className="mb-4">
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2 text-purple-900">
                <Brain className="h-6 w-6 text-purple-600" />
                AI Analysis Prompt
              </h2>
              <p className="text-gray-600">
                This is the complete system prompt sent to the AI model. It instructs the AI on how to analyze product images
                and what information to extract.
              </p>
            </div>

            <div className="space-y-4">
              {/* Prompt Writing Tips - Collapsible */}
              <div className="border border-green-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => {
                    const tipsContent = document.getElementById('prompt-tips-content');
                    const tipsChevron = document.getElementById('prompt-tips-chevron');
                    if (tipsContent && tipsChevron) {
                      const isHidden = tipsContent.classList.contains('hidden');
                      if (isHidden) {
                        tipsContent.classList.remove('hidden');
                        tipsChevron.classList.add('rotate-180');
                      } else {
                        tipsContent.classList.add('hidden');
                        tipsChevron.classList.remove('rotate-180');
                      }
                    }
                  }}
                  className="w-full p-4 bg-green-50 hover:bg-green-100 transition-colors flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <Info className="h-4 w-4 text-green-700" />
                    <h3 className="text-sm font-semibold text-green-900">💡 Prompt Writing Tips</h3>
                  </div>
                  <ChevronDown id="prompt-tips-chevron" className="h-4 w-4 text-green-700 transition-transform" />
                </button>
                <div id="prompt-tips-content" className="hidden p-4 bg-green-50 border-t border-green-200">
                  <ul className="text-sm text-green-800 space-y-1">
                    <li>• Be specific about what information you need extracted</li>
                    <li>• Explain what each image should contain (e.g., "Image 1: Front view")</li>
                    <li>• Use numbered lists or bullet points for clarity</li>
                    <li>• Specify the expected response format</li>
                    <li>• Include examples of good analysis when possible</li>
                    <li>• Test your prompt and iterate based on AI responses</li>
                  </ul>
                </div>
              </div>

              {/* Template Variables Info Box */}
              <div className="bg-purple-50 border-2 border-purple-200 rounded-lg">
                <button
                  type="button"
                  onClick={() => setTemplateVarsExpanded(!templateVarsExpanded)}
                  className="w-full p-4 flex items-center justify-between hover:bg-purple-100 transition-colors"
                >
                  <h3 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Template Variables Available
                  </h3>
                  <ChevronDown
                    className={`h-5 w-5 text-purple-700 transition-transform ${
                      templateVarsExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {templateVarsExpanded && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-purple-800 mb-3">
                      Use these variables in your prompt to inject dynamically generated attribute definitions:
                    </p>
                <div className="space-y-2">
                  {/* Dynamic section variables based on schema */}
                  {form.ai_analysis_schema?.sections && Object.keys(form.ai_analysis_schema.sections).length > 0 ? (
                    <>
                      {Object.keys(form.ai_analysis_schema.sections).map(sectionName => (
                        <TemplateVariableBox
                          key={sectionName}
                          variable={`{{ attributes:${sectionName} }}`}
                          description={
                            form.ai_analysis_schema.sections[sectionName].description ||
                            `Expands to ${sectionName.replace(/_/g, ' ')} attributes`
                          }
                          sectionName={sectionName}
                        />
                      ))}
                      <TemplateVariableBox
                        variable="{{ attributes:all }}"
                        description="Expands to ALL attribute sections combined"
                        sectionName="all"
                      />
                    </>
                  ) : (
                    <>
                      {/* Default variables when no schema is defined */}
                      <TemplateVariableBox
                        variable="{{ attributes:square_projection }}"
                        description="Expands to customer-facing attributes (synced to Square POS)"
                        sectionName="square_projection"
                      />
                      <TemplateVariableBox
                        variable="{{ attributes:internal_record }}"
                        description="Expands to internal inventory-specific attributes"
                        sectionName="internal_record"
                      />
                      <TemplateVariableBox
                        variable="{{ attributes:all }}"
                        description="Expands to ALL attribute sections combined"
                        sectionName="all"
                      />
                    </>
                  )}
                </div>
                <p className="text-xs text-purple-700 mt-3 italic">
                  💡 Click the <Eye className="inline h-3 w-3" /> icon to see live preview of what each variable expands to based on your current schema.
                </p>
              </div>
            )}
          </div>

              {/* Prompt Editor */}
              <div>
                <label htmlFor="ai_prompt" className="block text-sm font-bold text-gray-900 mb-2">
                  System Prompt
                </label>
                <textarea
                  id="ai_prompt"
                  value={form.ai_prompt}
                  onChange={(e) => handleChange('ai_prompt', e.target.value)}
                  rows={20}
                  className="w-full px-4 py-3 border-2 border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-base leading-relaxed bg-gray-50"
                  placeholder={`Enter the complete system prompt for AI product analysis.

This prompt instructs the AI on how to analyze product images and what information to extract.

Example:
─────────────────────────────────────────────────────────
You are an expert product analyst specializing in vintage Japanese textiles.

ANALYZE the provided images and extract the following information:

1. ITEM TYPE & CATEGORY
   - Specific garment type (kimono, haori, obi, etc.)
   - Subcategory and style variations

2. ERA/PERIOD
   - Historical period (Meiji, Taisho, Showa, Heisei, Contemporary)
   - Approximate date range based on:
     * Construction techniques
     * Pattern styles
     * Material characteristics
     * Dyeing methods

3. MATERIALS & CONSTRUCTION
   - Primary fabric (silk, cotton, wool, synthetic)
   - Weave type and quality
   - Lining materials
   - Construction methods (hand-sewn, machine-sewn)
   * Notable craftsmanship details

4. CONDITION ASSESSMENT
   - Overall condition (Excellent, Very Good, Good, Fair, Poor)
   - Specific wear patterns
   - Stains, tears, or damage
   - Structural integrity
   - Restoration needs

5. NOTABLE FEATURES
   - Unique design elements
   - Artistic motifs and symbolism
   - Cultural significance
   - Rarity indicators
   - Market value factors

EXPECTED IMAGES (${form.min_required_images} images required):
- Image 1: Front view (full garment)
- Image 2: Back view (full garment)
${form.min_required_images >= 3 ? '- Image 3: Detail view (pattern, label, or craftsmanship closeup)' : ''}

RESPONSE FORMAT:
Provide detailed analysis for each category above.
Be specific and reference which image shows each detail.
Flag any areas of uncertainty or required additional images.
─────────────────────────────────────────────────────────`}
                />
                <div className="mt-2 flex items-start gap-2 text-sm text-gray-600">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    Use clear, specific instructions. The AI performs best with well-structured prompts that explain
                    exactly what information to extract and how to format the response.
                  </p>
                </div>
              </div>

              {/* Prompt Statistics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Characters</div>
                  <div className="text-2xl font-bold text-gray-900">{form.ai_prompt.length.toLocaleString()}</div>
                </div>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Words</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {form.ai_prompt.trim() ? form.ai_prompt.trim().split(/\s+/).length.toLocaleString() : 0}
                  </div>
                </div>
                <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="text-sm text-gray-600">Lines</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {form.ai_prompt ? form.ai_prompt.split('\n').length.toLocaleString() : 0}
                  </div>
                </div>
              </div>
            </div>
          </Card>
            </>
          )}

          {/* Attribute Schema Tab */}
          {activeTab === 'schema' && (
            <Card>
              <div className="mb-4">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2 text-purple-900">
                  <Database className="h-6 w-6 text-purple-600" />
                  Attribute Schema Builder
                </h2>
                <p className="text-gray-600">
                  Define the structure of attributes that the AI will extract from product images.
                  These attributes will be injected into your prompt using template variables.
                </p>
              </div>

              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={handleLoadExampleSchema}
                    variant="outline"
                    size="md"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    Load Example (Koshinko Kimono)
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePreviewPrompt}
                    disabled={previewingPrompt || !form.ai_prompt}
                    variant="outline"
                    size="md"
                    className="border-blue-300 text-blue-600 hover:bg-blue-50"
                  >
                    {previewingPrompt ? 'Loading Preview...' : 'Preview Expanded Prompt'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handlePreviewApiRequest}
                    disabled={previewingApiRequest || !form.ai_prompt}
                    variant="outline"
                    size="md"
                    className="border-purple-300 text-purple-600 hover:bg-purple-50"
                  >
                    {previewingApiRequest ? 'Loading...' : 'View API Request'}
                  </Button>
                </div>

                {/* Visual Schema Builder */}
                <SchemaBuilder
                  schema={form.ai_analysis_schema}
                  onChange={(newSchema) => handleChange('ai_analysis_schema', newSchema)}
                />

                {/* JSON Preview (read-only) */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Info className="h-4 w-4 text-blue-600" />
                    <label className="block text-sm font-bold text-gray-900">
                      JSON Schema Preview (Read-Only)
                    </label>
                  </div>
                  <pre className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg bg-gray-50 font-mono text-xs overflow-auto max-h-96">
{form.ai_analysis_schema ? JSON.stringify(form.ai_analysis_schema, null, 2) : '{\n  "version": "1.0.0",\n  "sections": {}\n}'}
                  </pre>
                  <p className="text-xs text-gray-600 mt-2">
                    This is the generated JSON schema based on your visual configuration above.
                  </p>
                </div>

                {/* Prompt Preview Modal */}
                {promptPreview && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">Expanded Prompt Preview</h3>
                        <button
                          type="button"
                          onClick={() => setPromptPreview(null)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Close preview"
                          aria-label="Close preview"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="p-4 overflow-auto flex-1">
                        <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 p-4 rounded border border-gray-200">
{promptPreview}
                        </pre>
                      </div>
                      <div className="p-4 border-t border-gray-200 flex justify-end">
                        <Button
                          type="button"
                          onClick={() => setPromptPreview(null)}
                          variant="primary"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* API Request Preview Modal */}
                {apiRequestPreview && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                      <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">Claude API Request Preview</h3>
                        <button
                          type="button"
                          onClick={() => setApiRequestPreview(null)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Close preview"
                          aria-label="Close preview"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="p-4 overflow-auto flex-1">
                        {/* Token Estimates */}
                        {apiRequestPreview.notes && (
                          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-2">Request Details</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-blue-700">System Prompt Resolved:</span>{' '}
                                <span className="font-mono">{apiRequestPreview.notes.system_prompt_resolved ? 'Yes' : 'No'}</span>
                              </div>
                              <div>
                                <span className="text-blue-700">Template Variables Expanded:</span>{' '}
                                <span className="font-mono">{apiRequestPreview.notes.template_variables_expanded ? 'Yes' : 'No'}</span>
                              </div>
                              <div>
                                <span className="text-blue-700">Image Count:</span>{' '}
                                <span className="font-mono">{apiRequestPreview.notes.image_count}</span>
                              </div>
                              <div>
                                <span className="text-blue-700">Estimated Total Tokens:</span>{' '}
                                <span className="font-mono">{apiRequestPreview.notes.token_estimate?.total_estimated?.toLocaleString() || 'N/A'}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* API Request */}
                        <div className="mb-2">
                          <h4 className="font-semibold text-gray-900 mb-2">Complete POST Request</h4>
                        </div>
                        <pre className="whitespace-pre-wrap font-mono text-xs bg-gray-50 p-4 rounded border border-gray-200">
{JSON.stringify(apiRequestPreview.request, null, 2)}
                        </pre>
                      </div>
                      <div className="p-4 border-t border-gray-200 flex justify-end">
                        <Button
                          type="button"
                          onClick={() => setApiRequestPreview(null)}
                          variant="primary"
                        >
                          Close
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Definitions Tab */}
          {activeTab === 'definitions' && (
            <Card>
              <div className="mb-6">
                <h2 className="text-2xl font-bold mb-2 flex items-center gap-2 text-purple-900">
                  <Info className="h-6 w-6 text-purple-600" />
                  Enum Definitions
                </h2>
                <p className="text-gray-600">
                  Define what enum values mean for AI analysis. These definitions are injected into your prompt using <code className="text-purple-600 bg-purple-50 px-1 rounded">{'{{ definitions:type }}'}</code> template variables.
                </p>
              </div>

              <div className="space-y-8">
                <EnumDefinitionsManager
                  definitionType="Categories"
                  title="Categories"
                  organizationId={organizationId}
                  readOnlyNames={true}
                  squareSyncInfo="Category names are synced from Square (read-only). You can edit descriptions."
                />

                <EnumDefinitionsManager
                  definitionType="Eras"
                  title="Eras"
                  organizationId={organizationId}
                />

                <EnumDefinitionsManager
                  definitionType="Conditions"
                  title="Conditions"
                  organizationId={organizationId}
                />

                <EnumDefinitionsManager
                  definitionType="Materials"
                  title="Materials"
                  organizationId={organizationId}
                />

                <EnumDefinitionsManager
                  definitionType="Confidence Levels"
                  title="Confidence Levels"
                  organizationId={organizationId}
                />

                <EnumDefinitionsManager
                  definitionType="Boy's Ceremonial Motifs"
                  title="Boy's Ceremonial Motifs"
                  organizationId={organizationId}
                />

                <EnumDefinitionsManager
                  definitionType="Girl's Motifs"
                  title="Girl's Motifs"
                  organizationId={organizationId}
                />
              </div>
            </Card>
          )}

          {/* Actions */}
          <div className="flex justify-between items-center gap-4 sticky bottom-0 bg-white p-4 border-t-2 border-gray-200 -mx-4 -mb-4">
            <Button
              type="button"
              onClick={() => onNavigate('admin-organizations')}
              variant="outline"
              icon={X}
              size="lg"
            >
              Cancel
            </Button>

            <div className="flex gap-3">
              <Button
                type="button"
                onClick={() => onNavigate('admin-organizations')}
                variant="ghost"
                icon={History}
                size="lg"
                title="View AI configuration change history"
              >
                View History
              </Button>

              <Button
                type="submit"
                variant="primary"
                icon={Save}
                disabled={saving}
                size="lg"
                className="px-8"
              >
                {saving ? 'Saving...' : 'Save AI Configuration'}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
